import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import {
    useNodesState,
    useEdgesState,
    addEdge,
    applyNodeChanges,
    applyEdgeChanges
} from '@xyflow/react';
import { useApp } from './AppContext';
import { useAuth } from './AuthContext';
import { NODE_DEFAULTS, STAGE_POSITIONS, NODE_DIMENSIONS } from '../components/funnel-builder/nodes';
import { getFunnel, saveFunnel as saveFunnelToFirestore } from '../services/funnelService';
import { autoLayoutNodes } from '../utils/funnelHelpers';
import { db } from '../lib/firebase';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { getPillarsByBrand } from '../services/pillarService';

const FunnelBuilderContext = createContext();

// Helper to recursively remove undefined values (Firebase doesn't allow them)
const removeUndefinedValues = (obj) => {
    if (obj === null || typeof obj !== 'object') return obj;
    if (Array.isArray(obj)) {
        return obj.map(removeUndefinedValues);
    }
    const result = {};
    for (const [key, value] of Object.entries(obj)) {
        if (value !== undefined) {
            result[key] = removeUndefinedValues(value);
        }
    }
    return result;
};

export const useFunnelBuilder = () => {
    const context = useContext(FunnelBuilderContext);
    if (!context) {
        throw new Error('useFunnelBuilder must be used within FunnelBuilderProvider');
    }
    return context;
};

// Supported platforms
const PLATFORMS = ['meta', 'google', 'tiktok', 'linkedin'];

// Default viewport - zoomed out to see full funnel
const DEFAULT_VIEWPORT = { x: 50, y: 50, zoom: 0.65 };

export const FunnelBuilderProvider = ({ children }) => {
    const { selectedBrand } = useApp();
    const { currentUser } = useAuth();

    // Active platform tab
    const [activePlatform, setActivePlatform] = useState('meta');

    // Platform-specific funnel state
    const [platformFunnels, setPlatformFunnels] = useState(() => {
        const initial = {};
        PLATFORMS.forEach(platform => {
            initial[platform] = {
                id: null,
                name: '',
                nodes: [],
                edges: [],
                viewport: DEFAULT_VIEWPORT,
                status: 'draft',
                isDirty: false
            };
        });
        return initial;
    });

    // Get current platform's funnel
    const currentFunnel = platformFunnels[activePlatform];

    // React Flow state for current platform
    const [nodes, setNodes, onNodesChange] = useNodesState(currentFunnel.nodes);
    const [edges, setEdges, onEdgesChange] = useEdgesState(currentFunnel.edges);
    const [viewport, setViewport] = useState(currentFunnel.viewport);

    // Audience segments state
    const [segments, setSegments] = useState([]);

    // Retargeting pools state
    const [retargetingPools, setRetargetingPools] = useState([]);

    // Creative themes state
    const [creativeThemes, setCreativeThemes] = useState([]);

    // Budget allocation state
    const [budgetAllocation, setBudgetAllocation] = useState({
        mode: 'manual',
        stageAllocation: { TOFU: 40, MOFU: 35, BOFU: 25 },
        performanceRules: {
            reallocationFrequency: 'weekly',
            minSpendPerStage: 10,
            kpiWeights: { cpa: 0.4, roas: 0.4, ctr: 0.2 },
            optimizationGoal: 'balance'
        }
    });

    // Marketing pillar state
    const [availablePillars, setAvailablePillars] = useState([]);
    const [isLoadingPillars, setIsLoadingPillars] = useState(false);
    // Node-to-pillar mapping: { nodeId: { pillarId, pillarName } }
    const [nodePillarMapping, setNodePillarMapping] = useState({});

    // Modal states
    const [modalState, setModalState] = useState({
        campaign: { isOpen: false, mode: 'create', data: null },
        adset: { isOpen: false, mode: 'create', data: null },
        ad: { isOpen: false, mode: 'create', data: null },
        abtest: { isOpen: false, mode: 'create', data: null },
        segment: { isOpen: false, mode: 'create', data: null },
        retargetingPool: { isOpen: false, mode: 'create', data: null },
        creativeTheme: { isOpen: false, mode: 'create', data: null },
        budgetOptimizer: { isOpen: false, data: null },
        creativePicker: { isOpen: false, callback: null }
    });

    // Selected node for editing
    const [selectedNode, setSelectedNode] = useState(null);

    // Properties Panel state - for editing selected node
    const [selectedNodeData, setSelectedNodeData] = useState(null);
    const [originalNodeData, setOriginalNodeData] = useState(null);
    const [isFormDirty, setIsFormDirty] = useState(false);

    // Loading state
    const [isLoading, setIsLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);

    // Platform connections state - stores OAuth connection status for each platform
    const [platformConnections, setPlatformConnections] = useState(() => {
        const initial = {};
        PLATFORMS.forEach(platform => {
            initial[platform] = {
                isConnected: false,
                accountId: null,
                accountName: null,
                isLoading: false,
                error: null
            };
        });
        return initial;
    });

    // Fetch platform connection status from Firestore
    const fetchPlatformConnections = useCallback(async () => {
        if (!currentUser?.uid || !selectedBrand?.id) return;

        // Set all to loading
        setPlatformConnections(prev => {
            const updated = { ...prev };
            PLATFORMS.forEach(p => {
                updated[p] = { ...updated[p], isLoading: true, error: null };
            });
            return updated;
        });

        try {
            const connectionsRef = collection(db, 'platformConnections');
            const q = query(
                connectionsRef,
                where('userId', '==', currentUser.uid),
                where('brandId', '==', selectedBrand.id)
            );
            const snapshot = await getDocs(q);

            // Build connection map from results
            const connectionMap = {};
            PLATFORMS.forEach(p => {
                connectionMap[p] = {
                    isConnected: false,
                    accountId: null,
                    accountName: null,
                    isLoading: false,
                    error: null
                };
            });

            snapshot.forEach(doc => {
                const data = doc.data();
                if (PLATFORMS.includes(data.platform)) {
                    connectionMap[data.platform] = {
                        isConnected: true,
                        accountId: data.accountId || null,
                        accountName: data.accountName || data.accountId || null,
                        isLoading: false,
                        error: null,
                        connectionId: doc.id,
                        scopes: data.scopes || [],
                        connectedAt: data.createdAt
                    };
                }
            });

            setPlatformConnections(connectionMap);
        } catch (error) {
            console.error('Error fetching platform connections:', error);
            // Set error state
            setPlatformConnections(prev => {
                const updated = { ...prev };
                PLATFORMS.forEach(p => {
                    updated[p] = { ...updated[p], isLoading: false, error: error.message };
                });
                return updated;
            });
        }
    }, [currentUser?.uid, selectedBrand?.id]);

    // Fetch connections when user/brand changes
    useEffect(() => {
        fetchPlatformConnections();
    }, [fetchPlatformConnections]);

    // Load available pillars when brand changes
    useEffect(() => {
        const loadPillars = async () => {
            if (!selectedBrand?.id) {
                setAvailablePillars([]);
                return;
            }

            setIsLoadingPillars(true);
            try {
                const pillars = await getPillarsByBrand(selectedBrand.id);
                setAvailablePillars(pillars);
                console.log('[FunnelBuilderContext] Loaded pillars:', pillars.length);
            } catch (error) {
                console.error('[FunnelBuilderContext] Error loading pillars:', error);
            } finally {
                setIsLoadingPillars(false);
            }
        };

        loadPillars();
    }, [selectedBrand?.id]);

    // Initiate OAuth connection for a platform
    const initiateOAuthConnection = useCallback((platform) => {
        if (!selectedBrand?.id) {
            console.error('No brand selected');
            return;
        }

        // Build OAuth URL based on platform
        const baseUrl = window.location.origin;
        const state = JSON.stringify({
            brandId: selectedBrand.id,
            returnUrl: window.location.pathname
        });
        const encodedState = encodeURIComponent(state);

        let authUrl;
        switch (platform) {
            case 'meta':
                authUrl = `/api/auth/meta/connect?brandId=${selectedBrand.id}&state=${encodedState}`;
                break;
            case 'google':
                authUrl = `/api/auth/google-ads/connect?brandId=${selectedBrand.id}&state=${encodedState}`;
                break;
            case 'tiktok':
                authUrl = `/api/auth/tiktok/connect?brandId=${selectedBrand.id}&state=${encodedState}`;
                break;
            case 'linkedin':
                authUrl = `/api/auth/linkedin/connect?brandId=${selectedBrand.id}&state=${encodedState}`;
                break;
            default:
                console.error('Unknown platform:', platform);
                return;
        }

        // Open OAuth flow in popup or redirect
        window.location.href = authUrl;
    }, [selectedBrand?.id]);

    // Get connection for current platform
    const currentPlatformConnection = platformConnections[activePlatform];

    // Sync nodes/edges when platform changes
    useEffect(() => {
        const funnel = platformFunnels[activePlatform];
        setNodes(funnel.nodes);
        setEdges(funnel.edges);
        setViewport(funnel.viewport);
    }, [activePlatform]);

    // Update platform funnel when nodes/edges change
    useEffect(() => {
        setPlatformFunnels(prev => ({
            ...prev,
            [activePlatform]: {
                ...prev[activePlatform],
                nodes,
                edges,
                viewport,
                isDirty: true
            }
        }));
    }, [nodes, edges, viewport, activePlatform]);

    // Generate unique node ID
    const generateNodeId = useCallback((type) => {
        return `${type}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    }, []);

    // Calculate position for new node in funnel stage
    const calculateNodePosition = useCallback((type, funnelStage = 'TOFU', parentId = null) => {
        const stageX = STAGE_POSITIONS[funnelStage] || STAGE_POSITIONS.TOFU;
        const dimensions = NODE_DIMENSIONS[type] || { width: 200, height: 150 };

        // Find existing nodes in this stage
        const stageNodes = nodes.filter(n => n.data.funnelStage === funnelStage);
        const typeNodes = stageNodes.filter(n => n.type === type);

        // Calculate Y position based on existing nodes
        let yPos = 100;
        if (parentId) {
            const parentNode = nodes.find(n => n.id === parentId);
            if (parentNode) {
                yPos = parentNode.position.y + (NODE_DIMENSIONS[parentNode.type]?.height || 150) + 50;
            }
        } else if (typeNodes.length > 0) {
            const lastNode = typeNodes[typeNodes.length - 1];
            yPos = lastNode.position.y + dimensions.height + 30;
        } else {
            // Position based on type hierarchy
            // RetargetingPool and Segment are positioned as children of campaigns
            const typeOrder = {
                campaign: 0,
                retargetingPool: 1,
                segment: 1.5,
                adset: 2,
                abtest: 2.5,
                ad: 3
            };
            yPos = 100 + (typeOrder[type] || 0) * 180;
        }

        return { x: stageX, y: yPos };
    }, [nodes]);

    // Add a new node
    const addNode = useCallback((type, data = {}, parentId = null) => {
        const defaults = NODE_DEFAULTS[type];
        if (!defaults) {
            console.error(`Unknown node type: ${type}`);
            return null;
        }

        const id = generateNodeId(type);
        const funnelStage = data.funnelStage || defaults.data.funnelStage || 'TOFU';
        const position = calculateNodePosition(type, funnelStage, parentId);

        const newNode = {
            id,
            type,
            position,
            data: {
                ...defaults.data,
                ...data,
                funnelStage,
                onEdit: () => editNodeRef.current?.(id, type),
                onMenuClick: () => setSelectedNode(id)
            }
        };

        setNodes(prev => [...prev, newNode]);

        // Auto-connect to parent if provided
        if (parentId) {
            const parentNode = nodes.find(n => n.id === parentId);
            if (parentNode) {
                // Determine handles based on node types (inline to avoid dependency ordering)
                let sourceHandle = 'adset-out';
                let targetHandle = 'campaign-in';
                let isRetargetingConnection = false;

                // Source handle based on parent type and child type
                if (parentNode.type === 'campaign') {
                    if (type === 'retargetingPool' || type === 'segment' || type === 'campaign') {
                        sourceHandle = 'retarget-out';
                        isRetargetingConnection = true;
                    } else {
                        sourceHandle = 'adset-out';
                    }
                } else if (parentNode.type === 'adset') {
                    sourceHandle = 'ad-out';
                } else if (parentNode.type === 'abtest') {
                    sourceHandle = 'variant-out';
                } else if (parentNode.type === 'segment') {
                    sourceHandle = 'target-out';
                } else if (parentNode.type === 'retargetingPool') {
                    sourceHandle = 'pool-out';
                    isRetargetingConnection = true;
                }

                // Target handle based on child type
                if (type === 'campaign') {
                    targetHandle = 'retarget-in';
                } else if (type === 'adset') {
                    targetHandle = 'campaign-in';
                } else if (type === 'ad' || type === 'abtest') {
                    targetHandle = 'adset-in';
                } else if (type === 'segment') {
                    targetHandle = 'source-in';
                } else if (type === 'retargetingPool') {
                    targetHandle = 'pool-in';
                }

                const newEdge = {
                    id: `edge_${parentId}_${id}`,
                    source: parentId,
                    target: id,
                    sourceHandle,
                    targetHandle,
                    type: 'smart',
                    animated: isRetargetingConnection
                };
                setEdges(prev => [...prev, newEdge]);
            }
        }

        return id;
    }, [nodes, generateNodeId, calculateNodePosition, setNodes, setEdges]);

    // Update a node
    const updateNode = useCallback((nodeId, updates) => {
        setNodes(prev => prev.map(node => {
            if (node.id === nodeId) {
                return {
                    ...node,
                    data: {
                        ...node.data,
                        ...updates
                    }
                };
            }
            return node;
        }));
    }, [setNodes]);

    // Batch update multiple nodes
    const updateNodes = useCallback((updates) => {
        // updates is an array of { id, data } objects
        setNodes(prev => prev.map(node => {
            const update = updates.find(u => u.id === node.id);
            if (update) {
                return {
                    ...node,
                    data: {
                        ...node.data,
                        ...update.data
                    }
                };
            }
            return node;
        }));
    }, [setNodes]);

    // Delete a node and its children
    const deleteNode = useCallback((nodeId) => {
        // Find all child nodes (connected via edges)
        const childNodeIds = new Set();
        const findChildren = (id) => {
            edges.forEach(edge => {
                if (edge.source === id) {
                    childNodeIds.add(edge.target);
                    findChildren(edge.target);
                }
            });
        };
        findChildren(nodeId);

        // Delete node and children
        const nodesToDelete = new Set([nodeId, ...childNodeIds]);
        setNodes(prev => prev.filter(n => !nodesToDelete.has(n.id)));
        setEdges(prev => prev.filter(e =>
            !nodesToDelete.has(e.source) && !nodesToDelete.has(e.target)
        ));
    }, [edges, setNodes, setEdges]);

    // Determine the correct source handle based on source and target node types
    const getSourceHandleForConnection = useCallback((sourceType, targetType) => {
        // Campaign → RetargetingPool or Campaign → Segment should use 'retarget-out' (yellow dot on right)
        if (sourceType === 'campaign' && (targetType === 'retargetingPool' || targetType === 'segment')) {
            return 'retarget-out';
        }
        // Campaign → AdSet uses 'adset-out' (blue dot on bottom)
        if (sourceType === 'campaign' && targetType === 'adset') {
            return 'adset-out';
        }
        // Campaign → Campaign (retargeting between stages) uses 'retarget-out'
        if (sourceType === 'campaign' && targetType === 'campaign') {
            return 'retarget-out';
        }
        // AdSet → Ad or AdSet → ABTest
        if (sourceType === 'adset') {
            return 'ad-out';
        }
        // ABTest → Ad
        if (sourceType === 'abtest') {
            return 'variant-out';
        }
        // Segment → AdSet or Segment → Campaign
        if (sourceType === 'segment') {
            return 'target-out';
        }
        // RetargetingPool → Segment or RetargetingPool → AdSet
        if (sourceType === 'retargetingPool') {
            return 'pool-out';
        }
        return null;
    }, []);

    // Determine the correct target handle based on target node type
    const getTargetHandleForConnection = useCallback((_, targetType) => {
        // Connections into Campaign (retargeting)
        if (targetType === 'campaign') {
            return 'retarget-in';
        }
        // AdSet receives from Campaign, Segment, or RetargetingPool
        if (targetType === 'adset') {
            return 'campaign-in';
        }
        // Ad receives from AdSet or ABTest
        if (targetType === 'ad') {
            return 'adset-in';
        }
        // ABTest receives from AdSet
        if (targetType === 'abtest') {
            return 'adset-in';
        }
        // Segment receives from Campaign
        if (targetType === 'segment') {
            return 'source-in';
        }
        // RetargetingPool receives from Campaign
        if (targetType === 'retargetingPool') {
            return 'pool-in';
        }
        return null;
    }, []);

    // Handle new connections
    const onConnect = useCallback((params) => {
        // Find source and target node types
        const sourceNode = nodes.find(n => n.id === params.source);
        const targetNode = nodes.find(n => n.id === params.target);

        // Determine correct handles if not already specified
        let sourceHandle = params.sourceHandle;
        let targetHandle = params.targetHandle;

        if (sourceNode && targetNode) {
            if (!sourceHandle) {
                sourceHandle = getSourceHandleForConnection(sourceNode.type, targetNode.type);
            }
            if (!targetHandle) {
                targetHandle = getTargetHandleForConnection(sourceNode.type, targetNode.type);
            }
        }

        const newEdge = {
            ...params,
            id: `edge_${params.source}_${params.target}`,
            sourceHandle,
            targetHandle,
            type: 'smart',
            animated: sourceHandle?.includes('retarget') || sourceHandle === 'pool-out'
        };
        setEdges(prev => addEdge(newEdge, prev));
    }, [setEdges, nodes, getSourceHandleForConnection, getTargetHandleForConnection]);

    // Modal helpers
    const openModal = useCallback((type, mode = 'create', data = null) => {
        setModalState(prev => ({
            ...prev,
            [type]: { isOpen: true, mode, data }
        }));
    }, []);

    const closeModal = useCallback((type) => {
        setModalState(prev => ({
            ...prev,
            [type]: { isOpen: false, mode: 'create', data: null }
        }));
    }, []);

    const openCreativePicker = useCallback((callback) => {
        setModalState(prev => ({
            ...prev,
            creativePicker: { isOpen: true, callback }
        }));
    }, []);

    const closeCreativePicker = useCallback(() => {
        setModalState(prev => ({
            ...prev,
            creativePicker: { isOpen: false, callback: null }
        }));
    }, []);

    // Edit node by looking up current data from state
    const editNode = useCallback((nodeId, nodeType) => {
        // Find the current node from state to get fresh data
        const currentNode = nodes.find(n => n.id === nodeId);
        if (currentNode) {
            openModal(nodeType, 'edit', { id: nodeId, ...currentNode.data });
        }
    }, [nodes, openModal]);

    // Ref to always have latest editNode function (for use in callbacks)
    const editNodeRef = useRef(editNode);
    editNodeRef.current = editNode;

    // Handle node double-click (edit)
    const onNodeDoubleClick = useCallback((event, node) => {
        openModal(node.type, 'edit', { id: node.id, ...node.data });
    }, [openModal]);

    // === SEGMENT OPERATIONS ===
    const addSegment = useCallback((segmentData) => {
        const id = `segment_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        const newSegment = {
            id,
            name: segmentData.name || 'New Segment',
            type: segmentData.type || 'custom',
            sourceStages: segmentData.sourceStages || ['TOFU'],
            sourceCampaigns: segmentData.sourceCampaigns || [],
            intentSignals: {
                engagementDepth: { enabled: true, minAdViews: 2, minVideoWatchPercent: 50, clickThrough: false },
                websiteBehavior: { enabled: false, pagesVisited: [], minTimeOnSite: 30, cartActivity: false, formStarts: false },
                recencyFrequency: { enabled: true, lastInteractionDays: 14, minInteractions: 1 },
                stageProgression: { enabled: false, fromStage: 'TOFU', toStage: 'MOFU' },
                ...segmentData.intentSignals
            },
            estimatedSize: segmentData.estimatedSize || 0,
            funnelStage: segmentData.funnelStage || 'MOFU',
            color: segmentData.color || 'var(--color-accent)',
            createdAt: new Date().toISOString()
        };
        setSegments(prev => [...prev, newSegment]);
        return id;
    }, []);

    const updateSegment = useCallback((segmentId, updates) => {
        setSegments(prev => prev.map(seg =>
            seg.id === segmentId ? { ...seg, ...updates, updatedAt: new Date().toISOString() } : seg
        ));
    }, []);

    const deleteSegment = useCallback((segmentId) => {
        setSegments(prev => prev.filter(seg => seg.id !== segmentId));
        // Also remove any nodes referencing this segment
        setNodes(prev => prev.filter(n => n.data.segmentId !== segmentId));
    }, [setNodes]);

    // === RETARGETING POOL OPERATIONS ===
    const addRetargetingPool = useCallback((poolData) => {
        const id = `pool_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        const newPool = {
            id,
            name: poolData.name || 'New Retargeting Pool',
            type: poolData.type || 'engagement',
            sourceStage: poolData.sourceStage || 'TOFU',
            targetStage: poolData.targetStage || 'MOFU',
            sourceCampaigns: poolData.sourceCampaigns || [],
            trigger: {
                type: 'engagement',
                threshold: 1,
                lookbackDays: 30,
                ...poolData.trigger
            },
            estimatedSize: poolData.estimatedSize || 0,
            isActive: true,
            createdAt: new Date().toISOString()
        };
        setRetargetingPools(prev => [...prev, newPool]);
        return id;
    }, []);

    const updateRetargetingPool = useCallback((poolId, updates) => {
        setRetargetingPools(prev => prev.map(pool =>
            pool.id === poolId ? { ...pool, ...updates, updatedAt: new Date().toISOString() } : pool
        ));
    }, []);

    const deleteRetargetingPool = useCallback((poolId) => {
        setRetargetingPools(prev => prev.filter(pool => pool.id !== poolId));
        setNodes(prev => prev.filter(n => n.data.poolId !== poolId));
    }, [setNodes]);

    // Auto-generate retargeting pool from stage engagement
    const generateRetargetingPool = useCallback((sourceStage, targetStage, campaigns) => {
        const poolName = `${sourceStage} → ${targetStage} Engagers`;
        return addRetargetingPool({
            name: poolName,
            type: 'engagement',
            sourceStage,
            targetStage,
            sourceCampaigns: campaigns,
            trigger: { type: 'engagement', threshold: 1, lookbackDays: 30 }
        });
    }, [addRetargetingPool]);

    // === CREATIVE THEME OPERATIONS ===
    const addCreativeTheme = useCallback((themeData) => {
        const id = `theme_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        const newTheme = {
            id,
            name: themeData.name || 'New Theme',
            visual: {
                colorPalette: [],
                fontFamily: '',
                imageStyle: 'lifestyle',
                logoPlacement: 'top-right',
                ...themeData.visual
            },
            messaging: {
                toneOfVoice: 'professional',
                keyMessages: [],
                ctaProgression: {
                    TOFU: '',
                    MOFU: '',
                    BOFU: ''
                },
                ...themeData.messaging
            },
            assignedCampaigns: themeData.assignedCampaigns || [],
            assignedAds: themeData.assignedAds || [],
            createdAt: new Date().toISOString()
        };
        setCreativeThemes(prev => [...prev, newTheme]);
        return id;
    }, []);

    const updateCreativeTheme = useCallback((themeId, updates) => {
        setCreativeThemes(prev => prev.map(theme =>
            theme.id === themeId ? { ...theme, ...updates, updatedAt: new Date().toISOString() } : theme
        ));
    }, []);

    const deleteCreativeTheme = useCallback((themeId) => {
        setCreativeThemes(prev => prev.filter(theme => theme.id !== themeId));
    }, []);

    const assignThemeToCampaigns = useCallback((themeId, campaignIds) => {
        setCreativeThemes(prev => prev.map(theme =>
            theme.id === themeId
                ? { ...theme, assignedCampaigns: [...new Set([...theme.assignedCampaigns, ...campaignIds])] }
                : theme
        ));
    }, []);

    // === BUDGET ALLOCATION OPERATIONS ===
    const updateBudgetAllocation = useCallback((updates) => {
        setBudgetAllocation(prev => ({ ...prev, ...updates }));
    }, []);

    const calculateOptimalBudget = useCallback((performanceData) => {
        // Simple performance-based allocation algorithm
        const { TOFU, MOFU, BOFU } = performanceData || {};
        if (!TOFU || !MOFU || !BOFU) return budgetAllocation.stageAllocation;

        const weights = budgetAllocation.performanceRules.kpiWeights;

        // Calculate efficiency score for each stage
        const calcScore = (stage) => {
            const cpaScore = stage.cpa ? (1 / stage.cpa) * weights.cpa : 0;
            const roasScore = (stage.roas || 0) * weights.roas;
            const ctrScore = (stage.ctr || 0) * weights.ctr;
            return cpaScore + roasScore + ctrScore;
        };

        const scores = {
            TOFU: calcScore(TOFU),
            MOFU: calcScore(MOFU),
            BOFU: calcScore(BOFU)
        };

        const totalScore = scores.TOFU + scores.MOFU + scores.BOFU;
        if (totalScore === 0) return budgetAllocation.stageAllocation;

        const minSpend = budgetAllocation.performanceRules.minSpendPerStage;
        const remaining = 100 - (minSpend * 3);

        return {
            TOFU: Math.round(minSpend + (scores.TOFU / totalScore) * remaining),
            MOFU: Math.round(minSpend + (scores.MOFU / totalScore) * remaining),
            BOFU: Math.round(minSpend + (scores.BOFU / totalScore) * remaining)
        };
    }, [budgetAllocation]);

    // === PILLAR MAPPING OPERATIONS ===

    // Assign a pillar to a node (campaign or ad)
    const assignPillarToNode = useCallback((nodeId, pillarId) => {
        const pillar = availablePillars.find(p => p.id === pillarId);
        if (!pillar) {
            console.warn('[FunnelBuilderContext] Pillar not found:', pillarId);
            return;
        }

        setNodePillarMapping(prev => ({
            ...prev,
            [nodeId]: { pillarId: pillar.id, pillarName: pillar.name, pillarColor: pillar.color }
        }));

        // Also update the node data
        updateNode(nodeId, {
            pillarId: pillar.id,
            pillarName: pillar.name,
            pillarColor: pillar.color
        });

        console.log('[FunnelBuilderContext] Assigned pillar to node:', { nodeId, pillar: pillar.name });
    }, [availablePillars, updateNode]);

    // Remove pillar from a node
    const removePillarFromNode = useCallback((nodeId) => {
        setNodePillarMapping(prev => {
            const updated = { ...prev };
            delete updated[nodeId];
            return updated;
        });

        // Also update the node data
        updateNode(nodeId, {
            pillarId: null,
            pillarName: null,
            pillarColor: null
        });

        console.log('[FunnelBuilderContext] Removed pillar from node:', nodeId);
    }, [updateNode]);

    // Get pillar for a node
    const getNodePillar = useCallback((nodeId) => {
        const mapping = nodePillarMapping[nodeId];
        if (!mapping) return null;
        return availablePillars.find(p => p.id === mapping.pillarId) || null;
    }, [nodePillarMapping, availablePillars]);

    // Build URL with pillar context for Creative Chat
    const buildCreativeChatUrl = useCallback((nodeId, funnelStage) => {
        const node = nodes.find(n => n.id === nodeId);
        if (!node) return '/conversational';

        const params = new URLSearchParams();
        params.set('stage', funnelStage || node.data?.funnelStage || 'TOFU');
        params.set('funnelId', currentFunnel.id || '');
        params.set('campaignId', nodeId);

        if (node.data?.name) {
            params.set('campaignName', encodeURIComponent(node.data.name));
        }
        if (node.data?.objective) {
            params.set('objective', encodeURIComponent(node.data.objective));
        }

        // Include pillar context if assigned
        const pillarMapping = nodePillarMapping[nodeId];
        if (pillarMapping?.pillarId) {
            params.set('pillarId', pillarMapping.pillarId);
        }

        return `/conversational?${params.toString()}`;
    }, [nodes, currentFunnel.id, nodePillarMapping]);

    // Handle node click - populate Properties Panel form data
    const onNodeClick = useCallback((event, node) => {
        setSelectedNode(node.id);
        // Populate the form data for the Properties Panel
        const nodeData = { id: node.id, type: node.type, ...node.data };
        setSelectedNodeData(nodeData);
        setOriginalNodeData(nodeData);
        setIsFormDirty(false);
    }, []);

    // Handle pane click - deselect node
    const onPaneClick = useCallback(() => {
        // If there are unsaved changes, we could show a confirmation
        // For now, just clear the selection
        setSelectedNode(null);
        setSelectedNodeData(null);
        setOriginalNodeData(null);
        setIsFormDirty(false);
    }, []);

    // Update a field in the Properties Panel form
    const updateSelectedNodeData = useCallback((field, value) => {
        setSelectedNodeData(prev => {
            if (!prev) return prev;
            return { ...prev, [field]: value };
        });
        setIsFormDirty(true);
    }, []);

    // Save changes from Properties Panel to the node
    const saveSelectedNode = useCallback(() => {
        if (!selectedNodeData || !selectedNodeData.id) return;

        // Extract id and type, update the rest
        const { id, type, ...dataToSave } = selectedNodeData;
        updateNode(id, dataToSave);
        setOriginalNodeData(selectedNodeData);
        setIsFormDirty(false);
    }, [selectedNodeData, updateNode]);

    // Discard changes and revert to original
    const discardSelectedNodeChanges = useCallback(() => {
        setSelectedNodeData(originalNodeData);
        setIsFormDirty(false);
    }, [originalNodeData]);

    // Clear selection (for use when clicking canvas background)
    const clearSelection = useCallback(() => {
        setSelectedNode(null);
        setSelectedNodeData(null);
        setOriginalNodeData(null);
        setIsFormDirty(false);
    }, []);

    // Save modal data
    const saveModalData = useCallback((type, data) => {
        const mode = modalState[type].mode;
        const existingData = modalState[type].data;

        if (mode === 'edit' && existingData?.id) {
            updateNode(existingData.id, data);
        } else {
            // Determine parent for new node
            let parentId = null;
            if (type === 'adset' && selectedNode) {
                const selected = nodes.find(n => n.id === selectedNode);
                if (selected?.type === 'campaign') {
                    parentId = selectedNode;
                }
            } else if (type === 'ad' && selectedNode) {
                const selected = nodes.find(n => n.id === selectedNode);
                if (selected?.type === 'adset') {
                    parentId = selectedNode;
                }
            }

            addNode(type, data, parentId);
        }

        closeModal(type);
    }, [modalState, selectedNode, nodes, addNode, updateNode, closeModal]);

    // Calculate total budget
    const totalBudget = nodes
        .filter(n => n.type === 'campaign')
        .reduce((sum, n) => sum + (n.data.budget?.amount || 0), 0);

    // Get nodes by funnel stage
    const getNodesByStage = useCallback((stage) => {
        return nodes.filter(n => n.data.funnelStage === stage);
    }, [nodes]);

    // Save funnel to Firestore
    const saveFunnel = useCallback(async (name = null) => {
        if (!currentUser?.uid) {
            throw new Error('You must be logged in to save a funnel');
        }
        if (!selectedBrand?.id) {
            throw new Error('Please select a brand before saving');
        }

        setIsSaving(true);
        try {
            // Strip callback functions from nodes (can't be saved to Firestore)
            // Firebase doesn't allow undefined values, so we destructure them out
            const nodesToSave = nodes.map(node => {
                const { onEdit, onMenuClick, ...restData } = node.data;
                return {
                    ...node,
                    data: restData
                };
            });

            const funnelData = removeUndefinedValues({
                id: currentFunnel.id || null,
                name: name || currentFunnel.name || `${activePlatform} Funnel`,
                nodes: nodesToSave,
                edges,
                viewport,
                brandId: selectedBrand.id,
                userId: currentUser.uid,
                platform: activePlatform,
                status: currentFunnel.status || 'draft'
            });

            // Save to Firestore
            const savedFunnel = await saveFunnelToFirestore(funnelData);

            setPlatformFunnels(prev => ({
                ...prev,
                [activePlatform]: {
                    ...prev[activePlatform],
                    id: savedFunnel.id,
                    name: savedFunnel.name || funnelData.name,
                    nodes,
                    edges,
                    viewport,
                    status: savedFunnel.status || 'draft',
                    isDirty: false
                }
            }));

            return savedFunnel;
        } catch (error) {
            console.error('Error saving funnel:', error);
            throw error;
        } finally {
            setIsSaving(false);
        }
    }, [currentFunnel, nodes, edges, viewport, activePlatform, currentUser, selectedBrand]);

    // Load funnel from Firestore
    const loadFunnel = useCallback(async (funnelId) => {
        setIsLoading(true);
        try {
            const funnel = await getFunnel(funnelId);
            if (!funnel) {
                throw new Error('Funnel not found');
            }

            // Switch to the funnel's platform
            setActivePlatform(funnel.platform || 'meta');

            // Update the React Flow state - add callbacks that can't be stored in Firestore
            const loadedNodes = (funnel.nodes || []).map(node => ({
                ...node,
                data: {
                    ...node.data,
                    onEdit: () => editNodeRef.current?.(node.id, node.type),
                    onMenuClick: () => setSelectedNode(node.id)
                }
            }));
            const loadedEdges = funnel.edges || [];
            const loadedViewport = funnel.viewport || DEFAULT_VIEWPORT;

            setNodes(loadedNodes);
            setEdges(loadedEdges);
            setViewport(loadedViewport);

            // Update platform funnels state
            setPlatformFunnels(prev => ({
                ...prev,
                [funnel.platform || 'meta']: {
                    id: funnel.id,
                    name: funnel.name,
                    nodes: loadedNodes,
                    edges: loadedEdges,
                    viewport: loadedViewport,
                    status: funnel.status || 'draft',
                    isDirty: false
                }
            }));

            return funnel;
        } catch (error) {
            console.error('Error loading funnel:', error);
            throw error;
        } finally {
            setIsLoading(false);
        }
    }, [setNodes, setEdges]);

    // Clear current funnel
    const clearFunnel = useCallback(() => {
        setNodes([]);
        setEdges([]);
        setViewport(DEFAULT_VIEWPORT);
        setSelectedNode(null);
    }, [setNodes, setEdges]);

    // Rename current funnel
    const renameFunnel = useCallback((newName) => {
        setPlatformFunnels(prev => ({
            ...prev,
            [activePlatform]: {
                ...prev[activePlatform],
                name: newName,
                isDirty: true
            }
        }));
    }, [activePlatform]);

    // Apply a template to the canvas
    const applyTemplate = useCallback((template) => {
        if (!template || !template.nodes) return;

        // Generate unique IDs for all nodes
        const nodeIdMap = {};
        const nodeTypeMap = {}; // Track node types by index for handle determination
        const newNodes = template.nodes.map((node, idx) => {
            const id = generateNodeId(node.type);
            nodeIdMap[idx] = id;
            nodeTypeMap[idx] = node.type;

            // Merge defaults with template data
            const mergedData = {
                ...NODE_DEFAULTS[node.type]?.data,
                ...node.data
            };

            return {
                id,
                type: node.type,
                position: node.position || { x: 100 + idx * 150, y: 100 },
                data: {
                    ...mergedData,
                    onEdit: () => editNodeRef.current?.(id, node.type),
                    onMenuClick: () => setSelectedNode(id)
                }
            };
        });

        // Create edges using the index map with correct handles
        const newEdges = (template.edges || []).map(edge => {
            const sourceType = nodeTypeMap[edge.sourceIndex];
            const targetType = nodeTypeMap[edge.targetIndex];

            // Use template-provided handles or determine based on node types
            const sourceHandle = edge.sourceHandle || getSourceHandleForConnection(sourceType, targetType);
            const targetHandle = edge.targetHandle || getTargetHandleForConnection(sourceType, targetType);

            // Animate retargeting and pool connections
            const isRetargetingConnection = sourceHandle?.includes('retarget') || sourceHandle === 'pool-out';

            return {
                id: `edge_${nodeIdMap[edge.sourceIndex]}_${nodeIdMap[edge.targetIndex]}`,
                source: nodeIdMap[edge.sourceIndex],
                target: nodeIdMap[edge.targetIndex],
                sourceHandle,
                targetHandle,
                type: 'smart',
                animated: isRetargetingConnection
            };
        });

        // Apply autolayout to position nodes properly
        const layoutedNodes = autoLayoutNodes(newNodes, newEdges);

        // Clear and set new nodes/edges
        setNodes(layoutedNodes);
        setEdges(newEdges);
        setViewport(DEFAULT_VIEWPORT);
        setSelectedNode(null);
    }, [generateNodeId, openModal, setNodes, setEdges, getSourceHandleForConnection, getTargetHandleForConnection]);

    const value = {
        // Platform management
        activePlatform,
        setActivePlatform,
        platforms: PLATFORMS,

        // Platform connections (OAuth status)
        platformConnections,
        currentPlatformConnection,
        fetchPlatformConnections,
        initiateOAuthConnection,

        // Funnel state
        currentFunnel,
        nodes,
        edges,
        viewport,
        setViewport,

        // Node operations
        addNode,
        updateNode,
        updateNodes,
        deleteNode,
        selectedNode,
        setSelectedNode,

        // Properties Panel state
        selectedNodeData,
        originalNodeData,
        isFormDirty,
        updateSelectedNodeData,
        saveSelectedNode,
        discardSelectedNodeChanges,
        clearSelection,

        // React Flow handlers
        onNodesChange,
        onEdgesChange,
        onConnect,
        onNodeClick,
        onPaneClick,
        onNodeDoubleClick,

        // Modal management
        modalState,
        openModal,
        closeModal,
        saveModalData,
        openCreativePicker,
        closeCreativePicker,

        // Data helpers
        totalBudget,
        getNodesByStage,

        // Segment operations
        segments,
        addSegment,
        updateSegment,
        deleteSegment,

        // Retargeting pool operations
        retargetingPools,
        addRetargetingPool,
        updateRetargetingPool,
        deleteRetargetingPool,
        generateRetargetingPool,

        // Creative theme operations
        creativeThemes,
        addCreativeTheme,
        updateCreativeTheme,
        deleteCreativeTheme,
        assignThemeToCampaigns,

        // Budget allocation
        budgetAllocation,
        updateBudgetAllocation,
        calculateOptimalBudget,

        // Marketing pillar operations
        availablePillars,
        isLoadingPillars,
        nodePillarMapping,
        assignPillarToNode,
        removePillarFromNode,
        getNodePillar,
        buildCreativeChatUrl,

        // Persistence
        saveFunnel,
        loadFunnel,
        clearFunnel,
        renameFunnel,
        applyTemplate,
        isLoading,
        isSaving,
        isDirty: currentFunnel.isDirty
    };

    return (
        <FunnelBuilderContext.Provider value={value}>
            {children}
        </FunnelBuilderContext.Provider>
    );
};

export default FunnelBuilderContext;
